import { supabase } from './supabase';
import { createLogger } from './logger';
import { userDataCache } from './userDataCache';

const logger = createLogger({ module: 'AnalyticsService', level: 'info' });

/**
 * Maximum number of retry attempts for failed API calls
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Base delay for exponential backoff in milliseconds
 */
const BASE_RETRY_DELAY = 1000;

/**
 * Service for sending analytics data via Statsig
 * Note: This service requires Statsig to be initialized via StatsigProvider
 */
export class AnalyticsService {
  private static instance: AnalyticsService;
  private adminStatusCache: Map<string, { isAdmin: boolean, timestamp: number }> = new Map();
  private adminCacheExpiry = 5 * 60 * 1000; // 5 minutes

  private constructor() {
    logger.debug('AnalyticsService initialized with Statsig integration');
  }

  /**
   * Get the singleton instance of AnalyticsService
   */
  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  /**
   * Send user journey metrics via Statsig
   * Note: Requires Statsig to be initialized in the component context
   */
  public async sendJourneyMetrics(metrics: {
    journey_id: string;
    feature_name: string;
    step: string;
    outcome?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      // Use global Statsig instance if available
      if (typeof window !== 'undefined' && (window as any).statsig) {
        (window as any).statsig.logEvent('user_journey_step', {
          journey_id: metrics.journey_id,
          feature_name: metrics.feature_name,
          step: metrics.step,
          outcome: metrics.outcome,
          ...metrics.metadata
        });
        logger.debug('Journey metrics sent via Statsig', { feature: metrics.feature_name, step: metrics.step });
      } else {
        logger.debug('Statsig not available, skipping journey metrics');
      }
    } catch (error) {
      logger.error('Error sending journey metrics:', error);
    }
  }

  /**
   * Send feature usage metrics via Statsig
   */
  public async sendFeatureUsageMetrics(metrics: {
    feature_name: string;
    action: string;
    duration_ms?: number;
    success_rate?: number;
    error_count?: number;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      if (typeof window !== 'undefined' && (window as any).statsig) {
        (window as any).statsig.logEvent('feature_usage', {
          feature_name: metrics.feature_name,
          action: metrics.action,
          duration_ms: metrics.duration_ms,
          success_rate: metrics.success_rate,
          error_count: metrics.error_count,
          ...metrics.metadata
        });
        logger.debug('Feature usage metrics sent via Statsig', { feature: metrics.feature_name, action: metrics.action });
      } else {
        logger.debug('Statsig not available, skipping feature usage metrics');
      }
    } catch (error) {
      logger.error('Error sending feature usage metrics:', error);
    }
  }

  /**
   * Send performance metrics via Statsig
   */
  public async sendPerformanceMetrics(metrics: {
    route: string;
    load_time_ms: number;
    first_paint_ms?: number;
    time_to_interactive_ms?: number;
    memory_usage_mb?: number;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      if (typeof window !== 'undefined' && (window as any).statsig) {
        (window as any).statsig.logEvent('performance_metric', {
          route: metrics.route,
          load_time_ms: metrics.load_time_ms,
          first_paint_ms: metrics.first_paint_ms,
          time_to_interactive_ms: metrics.time_to_interactive_ms,
          memory_usage_mb: metrics.memory_usage_mb,
          ...metrics.metadata
        });
        logger.debug('Performance metrics sent via Statsig', { route: metrics.route, load_time: metrics.load_time_ms });
      } else {
        logger.debug('Statsig not available, skipping performance metrics');
      }
    } catch (error) {
      logger.error('Error sending performance metrics:', error);
    }
  }

  /**
   * Send a batch of metrics via Statsig
   */
  public async sendBatchMetrics(
    journeyMetrics: any[] = [],
    featureMetrics: any[] = [],
    performanceMetrics: any[] = []
  ): Promise<void> {
    try {
      // Send all metrics individually since Statsig doesn't have batch API
      const promises = [
        ...journeyMetrics.map(metric => this.sendJourneyMetrics(metric)),
        ...featureMetrics.map(metric => this.sendFeatureUsageMetrics(metric)),
        ...performanceMetrics.map(metric => this.sendPerformanceMetrics(metric))
      ];
      
      await Promise.allSettled(promises);
      logger.debug('Batch metrics sent via Statsig', { 
        journeyCount: journeyMetrics.length,
        featureCount: featureMetrics.length,
        performanceCount: performanceMetrics.length
      });
    } catch (error) {
      logger.error('Error sending batch metrics:', error);
    }
  }

  /**
   * Check if user is an analytics admin
   */
  public async isAnalyticsAdmin(userId: string): Promise<boolean> {
    try {
      // Check cache first
      const cachedStatus = this.adminStatusCache.get(userId);
      if (cachedStatus && (Date.now() - cachedStatus.timestamp) < this.adminCacheExpiry) {
        return cachedStatus.isAdmin;
      }

      // First check if user has admin type using userDataCache
      const isAdmin = await userDataCache.isUserAdmin(userId);
      
      if (!isAdmin) {
        logger.debug('User is not an admin type:', { userId });
        // Cache the negative result
        this.adminStatusCache.set(userId, {
          isAdmin: false,
          timestamp: Date.now()
        });
        return false;
      }

      // Then check if user is in the analytics_admin_users table
      const { data: adminData, error: adminError } = await supabase
        .from('analytics_admin_users')
        .select('user_id')
        .eq('user_id', userId)
        .single();

      if (adminError) {
        logger.error('Error checking analytics admin status:', adminError);
        return false;
      }

      // User is an admin if they're in the analytics_admin_users table
      const isAnalyticsAdmin = !!adminData;
      
      // Cache the result
      this.adminStatusCache.set(userId, {
        isAdmin: isAnalyticsAdmin,
        timestamp: Date.now()
      });

      return isAnalyticsAdmin;
    } catch (e) {
      logger.error('Exception in isAnalyticsAdmin:', e);
      return false;
    }
  }

  /**
   * Complete a journey via Statsig
   */
  public async completeJourney(journeyId: string, outcome: string, metadata?: Record<string, any>) {
    try {
      if (typeof window !== 'undefined' && (window as any).statsig) {
        (window as any).statsig.logEvent('journey_completed', {
          journey_id: journeyId,
          outcome,
          ...metadata
        });
        logger.debug('Journey completed via Statsig', { journeyId, outcome });
      } else {
        logger.debug('Statsig not available, skipping journey completion');
      }
    } catch (error) {
      logger.error('Error completing journey:', error);
    }
  }
}

// Export a singleton instance
export const analyticsService = AnalyticsService.getInstance(); 